import { plainText } from './inline';
import type { BlogPost, Block } from './types';

export const SITE_URL = 'https://easytocook.com.au';
export const SITE_NAME = 'EasyToCook';
export const BLOG_TITLE = 'EasyToCook Blog';
export const BLOG_DESCRIPTION =
  'Guides and comparisons for home cooks: saving recipes from Instagram and TikTok, planning the week, shopping smarter and tracking macros.';

export const postUrl = (post: Pick<BlogPost, 'slug'>) => `${SITE_URL}/blog/${post.slug}`;
export const blogUrl = () => `${SITE_URL}/blog`;

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

export interface TocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

/** H2s plus app-review cards (they are the h3s readers jump to). */
export const tableOfContents = (post: BlogPost): TocEntry[] =>
  post.body.flatMap((b): TocEntry[] => {
    if (b.type === 'h2') return [{ id: b.id, text: b.text, level: 2 }];
    if (b.type === 'appReview') return [{ id: b.id, text: `${b.rank}. ${b.name}`, level: 3 }];
    return [];
  });

const faqItems = (blocks: Block[]) =>
  blocks.flatMap((b) => (b.type === 'faq' ? b.items : []));

/** JSON-LD graph: BlogPosting + FAQPage (when the post has one) + breadcrumbs. */
export const postJsonLd = (post: BlogPost) => {
  const url = postUrl(post);
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'BlogPosting',
      '@id': `${url}#article`,
      mainEntityOfPage: url,
      headline: post.title,
      description: post.description,
      image: `${SITE_URL}${post.ogImage}`,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt,
      author: { '@type': 'Organization', name: post.author.name, url: SITE_URL },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
      },
      keywords: post.tags.join(', '),
      inLanguage: 'en-AU',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: blogUrl() },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
  ];
  const faq = faqItems(post.body);
  if (faq.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: faq.map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: { '@type': 'Answer', text: plainText(q.answer) },
      })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
};

export const blogJsonLd = (posts: BlogPost[]) => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${blogUrl()}#blog`,
  url: blogUrl(),
  name: BLOG_TITLE,
  description: BLOG_DESCRIPTION,
  publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  blogPost: posts.map((p) => ({
    '@type': 'BlogPosting',
    headline: p.title,
    url: postUrl(p),
    datePublished: p.publishedAt,
    dateModified: p.updatedAt,
  })),
});
